import styled from '@emotion/styled-base';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Form,
    FormInputText,
    FormInputDatePicker,
    HeadingText40,
    useForm,
    FormInputSelect,
    Button,
    ButtonGroup,
} from '@modernhealth/vital-components';
import { useSelector } from 'react-redux';

import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { usaStatesOptions } from 'src/shared/models/statesOptions';
import { sexIdentityOptions } from '../models/sexIdentityOptions';
import { isPolicyHolder, addDashToZip, getMatchingUSAState } from '../helpers/insuranceHelpers';

export interface InsuranceRelationshipOption {
    code: string;
    description: string;
}
export interface InsuranceMemberData {
    firstName?: string;
    lastName?: string;
    sex?: string;
    birthDate?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    relationshipCode?: string;
}

export interface InsurancePlanData {
    policyMemberId?: string;
    policyNumber: string;
    primaryFirstName?: string;
    primaryLastName?: string;
}

export interface Props {
    onCancel: () => void;
    onFormSubmit: (formData: InsuranceMemberData & InsurancePlanData) => void;
    relationshipOptions: InsuranceRelationshipOption[];
    isLoading: boolean;
}

const SUBSCRIBER_ID_MIN_LENGTH = 6;
const SUBSCRIBER_ID_MAX_LENGTH = 80;
const POLICY_NUMBER_MIN_LENGTH = 2;
const POLICY_NUMBER_MAX_LENGTH = 80;
const FIRST_NAME_MAX_LENGTH = 25;
const LAST_NAME_MAX_LENGTH = 25;
const ADDRESS_MAX_LENGTH = 95;
const ADDRESS_CITY_MAX_LENGTH = 30;
const ADDRESS_ZIP_REGEX = /^(\d{5})([- ]\d{4})?$/;
const ONLY_ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]*$/;

const FormContainer = styled('div')({
    width: '100%',
});

const FormRow = styled('div')({
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
});

export const EnterInsuranceInfoForm = (props: Props) => {
    const userDefaultData = useSelector(getUserInformation);
    const { t } = useTranslation();

    const formSchema = Yup.object().shape({
        firstName: Yup.string()
            .required(t('validation.required.firstName'))
            .max(
                FIRST_NAME_MAX_LENGTH,
                t('insuranceForm:insurance_form.first_name_error', {
                    max: FIRST_NAME_MAX_LENGTH,
                }),
            ),
        lastName: Yup.string()
            .required(t('validation.required.lastName'))
            .max(
                LAST_NAME_MAX_LENGTH,
                t('insuranceForm:insurance_form.last_name_error', {
                    max: LAST_NAME_MAX_LENGTH,
                }),
            ),
        sex: Yup.string().trim().required(t('insuranceForm:insurance_form.sex.required')),
        birthDate: Yup.string().required(t('validation:birth_date.required')),
        addressLine1: Yup.string()
            .required(t('insuranceForm:insurance_form.address1.required'))
            .max(
                ADDRESS_MAX_LENGTH,
                t('insuranceForm:insurance_form.address1.max_number', {
                    max: ADDRESS_MAX_LENGTH,
                }),
            ),
        addressLine2: Yup.string(),
        addressCity: Yup.string()
            .required(t('insuranceForm:insurance_form.city.required'))
            .max(
                ADDRESS_CITY_MAX_LENGTH,
                t('insuranceForm:insurance_form.address_city_error', {
                    max: ADDRESS_CITY_MAX_LENGTH,
                }),
            ),
        addressState: Yup.string()
            .trim()
            .required(t('insuranceForm:insurance_form.state.required')),
        addressZip: Yup.string()
            .required(t('insuranceForm:insurance_form.zip.required'))
            .matches(ADDRESS_ZIP_REGEX, t('insuranceForm:insurance_form.address_zip_error')),
        relationshipCode: Yup.string()
            .trim()
            .required(t('insuranceForm:insurance_form.relationship.required')),
        policyMemberId: Yup.string()
            .required(t('insuranceForm:insurance_form.subscriber_id.required'))
            .matches(
                ONLY_ALPHANUMERIC_REGEX,
                t('insuranceForm:insurance_form.subscriber_id.only_alphanumeric'),
            )
            .min(
                SUBSCRIBER_ID_MIN_LENGTH,
                t('insuranceForm:insurance_form.subscriber_id.min_number', {
                    min: SUBSCRIBER_ID_MIN_LENGTH,
                }),
            )
            .max(
                SUBSCRIBER_ID_MAX_LENGTH,
                t('insuranceForm:insurance_form.subscriber_id.max_number', {
                    max: SUBSCRIBER_ID_MAX_LENGTH,
                }),
            ),
        policyNumber: Yup.string()
            .required(t('insuranceForm:insurance_form.policy_number.required'))
            .matches(
                ONLY_ALPHANUMERIC_REGEX,
                t('insuranceForm:insurance_form.policy_number.only_alphanumeric'),
            )
            .min(
                POLICY_NUMBER_MIN_LENGTH,
                t('insuranceForm:insurance_form.policy_number.min_number', {
                    min: POLICY_NUMBER_MIN_LENGTH,
                }),
            )
            .max(
                POLICY_NUMBER_MAX_LENGTH,
                t('insuranceForm:insurance_form.policy_number.max_number', {
                    max: POLICY_NUMBER_MAX_LENGTH,
                }),
            ),
        primaryFirstName: Yup.string().when('relationshipCode', {
            is: relationshipCode => !isPolicyHolder(relationshipCode),
            then: Yup.string()
                .required(t('insuranceForm:insurance_form.primaryFirstName.required'))
                .max(FIRST_NAME_MAX_LENGTH, t('insuranceForm:insurance_form.first_name_error')),
        }),
        primaryLastName: Yup.string().when('relationshipCode', {
            is: relationshipCode => !isPolicyHolder(relationshipCode),
            then: Yup.string()
                .required(t('insuranceForm:insurance_form.primaryLastName.required'))
                .max(LAST_NAME_MAX_LENGTH, t('insuranceForm:insurance_form.last_name_error')),
        }),
    });

    const insuranceFormDefinition = useForm<InsuranceMemberData & InsurancePlanData>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            firstName: userDefaultData.firstName ?? '',
            lastName: userDefaultData.lastName ?? '',
            birthDate: userDefaultData.birthDate ?? undefined,
            addressLine1: userDefaultData.mailingAddress,
            addressCity: userDefaultData.city ?? '',
            addressState: getMatchingUSAState(userDefaultData.state)?.stateCode,
            addressZip: userDefaultData.zipCode ?? '',
        },
    });

    const relationshipCode = insuranceFormDefinition.watch('relationshipCode', '');
    const addressZip = insuranceFormDefinition.watch('addressZip');

    useEffect(() => {
        if (!!addressZip) insuranceFormDefinition.setValue('addressZip', addDashToZip(addressZip));
    }, [addressZip]);

    return (
        <FormContainer>
            <Form onSubmit={props.onFormSubmit} definition={insuranceFormDefinition}>
                <HeadingText40>{t('insuranceForm:insurance_form.form_heading')}</HeadingText40>

                <FormRow>
                    <FormInputText
                        name="firstName"
                        label={t('auth:registration_form.labels.firstName')}
                        required
                        trim
                    />
                    <FormInputText
                        name="lastName"
                        label={t('auth:registration_form.labels.lastName')}
                        required
                        trim
                    />
                </FormRow>

                <FormRow>
                    <FormInputDatePicker
                        name="birthDate"
                        label={t('auth:registration_form.labels.dateOfBirth')}
                        required
                    />
                </FormRow>

                <FormRow>
                    <FormInputSelect
                        name="sex"
                        label={t('insuranceForm:insurance_form.sex')}
                        options={sexIdentityOptions.map(({ value, translationId }) => ({
                            value,
                            label: t(translationId),
                        }))}
                        required
                    />
                </FormRow>

                <FormRow>
                    <FormInputText
                        name="addressLine1"
                        label={t('insuranceForm:insurance_form.address1')}
                        required
                        trim
                    />
                    <FormInputText
                        name="addressLine2"
                        label={t('insuranceForm:insurance_form.address2')}
                        trim
                    />
                </FormRow>

                <FormRow>
                    <FormInputText
                        name="addressCity"
                        label={t('providers:additional_info.city')}
                        required
                        trim
                    />
                </FormRow>

                <FormRow>
                    <FormInputSelect
                        name="addressState"
                        label={t('providers:additional_info.state')}
                        options={usaStatesOptions.map(state => ({
                            value: state.stateCode.toLowerCase(),
                            label: state.fullName,
                        }))}
                        required
                    />
                    <FormInputText
                        name="addressZip"
                        label={t('providers:additional_info.postal_code')}
                        required
                        trim
                    />
                </FormRow>

                <HeadingText40 marginTop={8}>
                    {t('insuranceForm:insurance_form.relationship.heading')}
                </HeadingText40>

                <FormRow>
                    <FormInputSelect
                        name="relationshipCode"
                        label={t('insuranceForm:insurance_form.relationship.placeholder')}
                        options={props.relationshipOptions.map(option => ({
                            value: option.code,
                            label: option.description,
                        }))}
                        required
                    />
                </FormRow>

                {relationshipCode && !isPolicyHolder(relationshipCode) && (
                    <FormRow>
                        <FormInputText
                            name="primaryFirstName"
                            label={t('insuranceForm:insurance_form.relationship.first_name')}
                            required
                            trim
                        />
                        <FormInputText
                            name="primaryLastName"
                            label={t('insuranceForm:insurance_form.relationship.last_name')}
                            required
                            trim
                        />
                    </FormRow>
                )}

                {relationshipCode && (
                    <>
                        <FormRow>
                            <FormInputText
                                name="policyMemberId"
                                label={
                                    isPolicyHolder(relationshipCode)
                                        ? t(
                                              'insuranceForm:insurance_form.insurance_subscriber_member_id',
                                          )
                                        : t(
                                              'insuranceForm:insurance_form.insurance_holder_subscriber_member_id',
                                          )
                                }
                                required
                                trim
                            />
                        </FormRow>
                        <FormRow>
                            <FormInputText
                                name="policyNumber"
                                label={
                                    isPolicyHolder(relationshipCode)
                                        ? t('insuranceForm:insurance_form.group_number')
                                        : t('insuranceForm:insurance_form.holder_group_number')
                                }
                                required
                                trim
                            />
                        </FormRow>
                    </>
                )}

                <ButtonGroup>
                    <Button variant="secondary" size="large" onClick={props.onCancel}>
                        {t('translation:common.cancel')}
                    </Button>
                    <Button type="submit" size="large" busy={props.isLoading}>
                        {t('translation:common.submit')}
                    </Button>
                </ButtonGroup>
            </Form>
        </FormContainer>
    );
};
